/**
 * The home page's optional widgets, saved per user as one preference. Nothing
 * audit-related is on the home page by default (Ian, 2026-09-28); a person
 * adds a widget from "Add widget" and it is theirs alone. Today one widget
 * exists, in two sizes (Ian, 2026-10-09: card C "as our Medium widget …
 * i also want there to be a large widget").
 */
export const HOME_WIDGETS_PREFERENCE = "home_widgets"

export type WidgetSize = "medium" | "large"
export type WidgetType = "not_on_website"

export interface HomeWidget {
  type: WidgetType
  size: WidgetSize
  /** The website the widget watches, by name ("dolanautogroup.com"). One
   * widget per monitored site, so a second site such as dolanrenotoyota.com
   * becomes its own card (Ian, 2026-10-09). Null when the group has no site
   * configured yet. */
  site: string | null
}

/** Two widgets are the same card when they watch the same site. */
export function sameWidget(a: HomeWidget, b: HomeWidget): boolean {
  return a.type === b.type && (a.site ?? null) === (b.site ?? null)
}

const WIDGET_TYPES: readonly WidgetType[] = ["not_on_website"]
const WIDGET_SIZES: readonly WidgetSize[] = ["medium", "large"]

/** A stored preference back into widgets; anything malformed is dropped, not thrown. */
export function parseHomeWidgets(raw: string | undefined | null): HomeWidget[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const widgets: HomeWidget[] = []
    for (const entry of parsed) {
      if (typeof entry !== "object" || entry === null) continue
      const { type, size, site } = entry as { type?: unknown; size?: unknown; site?: unknown }
      if (!WIDGET_TYPES.includes(type as WidgetType)) continue
      widgets.push({
        type: type as WidgetType,
        size: WIDGET_SIZES.includes(size as WidgetSize) ? (size as WidgetSize) : "medium",
        site: typeof site === "string" && site ? site : null,
      })
    }
    return widgets
  } catch {
    return []
  }
}

export function serializeHomeWidgets(widgets: HomeWidget[]): string {
  return JSON.stringify(widgets)
}
